import type { CSSProperties, ReactElement, ReactNode } from 'react';

import styles from './TransparentOverlay.module.css';

/**
 * Перенос `lib/src/presentation/widgets/route_pages/transparent_page.dart`.
 *
 * `TransparentPage` — маршрут поверх предыдущего: `opaque: false`, значит
 * экран под ним остаётся отрисованным, `barrierColor` затемняет его, а
 * ребёнок проявляется затуханием. Так открывается страница источника
 * поверх чата (она передаёт `Colors.transparent`, то есть без затемнения).
 *
 * В вебе маршрут вложен в чат и приходит в его `<Outlet />`, поэтому
 * оверлей сам берёт на себя то, что во Flutter делал навигатор:
 * `position: fixed` на весь экран, отдельный слой шторки и проявление
 * содержимого.
 *
 * Чего не удалось повторить: обратной анимации нет. Во Flutter уходящий
 * маршрут гас 300 мс, а React Router снимает вложенный экран сразу — такой
 * же приём, как в `RouteFade`, потребовал бы правки `ChatPage`, которая
 * рисует `<Outlet />`.
 */

/** `barrierColor ?? Colors.black.withValues(alpha: 0.8)` */
const DEFAULT_BARRIER_COLOR = 'rgba(0, 0, 0, 0.8)';

export interface TransparentOverlayProps {
  /** `backgroundColor` у `TransparentPage` — цвет шторки под содержимым. */
  readonly backgroundColor?: string;
  readonly children: ReactNode;
}

export function TransparentOverlay({
  backgroundColor = DEFAULT_BARRIER_COLOR,
  children,
}: TransparentOverlayProps): ReactElement {
  return (
    <div className={styles.root}>
      <div
        className={styles.barrier}
        style={{ '--barrier-color': backgroundColor } as CSSProperties}
        aria-hidden="true"
      />
      <div className={styles.child}>{children}</div>
    </div>
  );
}
